import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { flattenToAppURL } from '@plone/volto/helpers';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { useDispatch, useSelector } from 'react-redux';
import { getContent } from '@plone/volto/actions';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import arrowRightSVG from 'fcmh/theme/assets/icons/arrow-right.svg';
import config from '@plone/volto/registry';

const safe = (s) => (typeof s === 'string' ? s : '');

const normalizeRel = (val) => {
  if (!val) return null;
  if (Array.isArray(val)) return val[0] || null;
  return val;
};

const normalizeId = (val) => {
  const rel = normalizeRel(val);
  if (!rel) return null;
  if (typeof rel === 'string') return rel;
  return rel['@id'] || null;
};

const getTeaserImageInfoFromBlockData = (href, overrideImage) => {
  if (!href && !overrideImage) return null;

  const urlFromOverride = overrideImage?.['@id'];
  if (urlFromOverride && !overrideImage?.image_field) {
    const base = flattenToAppURL(urlFromOverride);
    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    return {
      useDirectUrl: true,
      src: origin ? `${origin}${base}` : base,
    };
  }

  if (href?.hasPreviewImage || href?.image_field || overrideImage) {
    return {
      useDirectUrl: false,
      item: overrideImage || href,
      imageField: overrideImage
        ? overrideImage.image_field
        : href?.image_field || 'image',
    };
  }

  return null;
};

const extractPreviewCopyright = (item) => {
  if (!item) return '';

  const direct =
    item.preview_caption ||
    item.previewCaption ||
    item.preview_image_caption ||
    item.previewImageCaption ||
    item.preview_image_legend ||
    item.previewImageLegend ||
    item.image_caption ||
    item.imageCaption ||
    item.legend ||
    item.rights ||
    item.copyright ||
    '';

  const nested =
    item.preview_image?.caption ||
    item.preview_image?.legend ||
    item.preview_image?.rights ||
    item.preview_image?.copyright ||
    '';

  const val = direct || nested;

  return safe(val).trim();
};

export function useTeaserData(data = {}) {
  const dispatch = useDispatch();

  const sourceId = normalizeId(data?.item);
  const appURL = sourceId ? flattenToAppURL(sourceId) : null;

  const sub = useSelector((state) =>
    appURL ? state.content.subrequests?.[appURL] : null,
  );
  const sourceContent = sub?.data;

  React.useEffect(() => {
    if (appURL && !sourceContent && !sub?.loading) {
      dispatch(getContent(appURL, null, appURL));
    }
  }, [appURL, sourceContent, sub?.loading, dispatch]);

  const href = normalizeRel(data?.item);
  const overrideImage = normalizeRel(data?.image);

  const sourceTitle = safe(sourceContent?.title);
  const sourceDesc =
    safe(sourceContent?.description) ||
    safe(sourceContent?.['@component']?.description) ||
    safe(sourceContent?.Description) ||
    '';

  const title = safe(data?.title) || sourceTitle;
  const kicker = safe(data?.kicker);
  const description = safe(data?.description) || sourceDesc;

  const imageInfo = getTeaserImageInfoFromBlockData(href, overrideImage);

  const buttonText = safe(data?.buttonText || 'Weiterlesen');
  const buttonLink =
    data?.buttonLink ||
    (sourceContent?.['@id'] ? flattenToAppURL(sourceContent['@id']) : null);

  const showButton = !data?.hideButton;
  const flagAlign = data?.flagAlign || 'left';
  const alignment = flagAlign === 'right' ? 'image-right' : 'image-left';
  const background = data?.background_color || 'gray';

  const copyrightText =
    extractPreviewCopyright(sourceContent) || extractPreviewCopyright(href);

  return {
    title,
    kicker,
    description,
    imageInfo,
    buttonText,
    buttonLink,
    showButton,
    alignment,
    background,
    copyrightText,
  };
}

export function Teaser({
  title,
  kicker,
  description,
  imageInfo,
  buttonText,
  buttonLink,
  showButton,
  alignment,
  background,
  isEditMode,
  copyrightText,
}) {
  const Image = config.getComponent('Image').component;

  const [copyrightOpen, setCopyrightOpen] = React.useState(false);

  const renderButton = () => (
    <span className="fcmh-teaser__button" aria-hidden="true">
      <Icon name={arrowRightSVG} size="16px" />
      {buttonText || 'Weiterlesen'}
    </span>
  );

  const renderImage = () => {
    if (!imageInfo) return null;

    if (imageInfo.useDirectUrl) {
      return (
        <img
          src={imageInfo.src}
          alt=""
          loading="lazy"
          className="fcmh-teaser__image"
        />
      );
    }

    return (
      <Image
        item={imageInfo.item}
        imageField={imageInfo.imageField}
        alt=""
        loading="lazy"
        className="fcmh-teaser__image"
        responsive={true}
      />
    );
  };

  const renderCopyright = () => {
    if (!copyrightText) return null;

    const toggle = (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      setCopyrightOpen((o) => !o);
    };

    return (
      <div
        className={cx('fcmh-teaser__copyright', {
          'is-open': copyrightOpen,
        })}
        onMouseLeave={() => setCopyrightOpen(false)}
      >
        <button
          type="button"
          className="fcmh-teaser__copyright-icon"
          aria-label="Bildnachweis anzeigen"
          aria-expanded={copyrightOpen}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setCopyrightOpen(false);
            }
          }}
        >
          ©
        </button>

        <div className="fcmh-teaser__copyright-tooltip" role="tooltip">
          <span className="fcmh-teaser__copyright-text">{copyrightText}</span>
        </div>
      </div>
    );
  };

  const teaserClasses = cx(
    'fcmh-teaser',
    `fcmh-teaser--${alignment}`,
    `fcmh-teaser--bg-${background}`,
    { 'has-no-button': !showButton },
  );

  const teaserContent = (
    <>
      <div className="fcmh-teaser__media">
        {imageInfo ? (
          <div className="fcmh-teaser__image-wrapper">
            {renderImage()}
            <div className="fcmh-teaser__image-overlay" aria-hidden="true" />
            {renderCopyright()}
          </div>
        ) : (
          <div className="fcmh-teaser__image fcmh-teaser__image--placeholder">
            {isEditMode ? 'Kein Bild gewählt' : ''}
          </div>
        )}
      </div>

      <div className="fcmh-teaser__content">
        {kicker ? <div className="fcmh-teaser__kicker">{kicker}</div> : null}
        {title ? <h2 className="fcmh-teaser__title">{title}</h2> : null}
        {description ? (
          <p className="fcmh-teaser__desc">{description}</p>
        ) : null}
        {showButton ? renderButton() : null}
      </div>
    </>
  );

  if (buttonLink && !isEditMode) {
    return (
      <UniversalLink
        href={buttonLink}
        className={cx(teaserClasses, 'fcmh-teaser__link-wrapper')}
        aria-label={title || buttonText}
      >
        {teaserContent}
      </UniversalLink>
    );
  }

  return (
    <section className={teaserClasses}>
      {teaserContent}
    </section>
  );
}

Teaser.propTypes = {
  title: PropTypes.string,
  kicker: PropTypes.string,
  description: PropTypes.string,
  imageInfo: PropTypes.object,
  buttonText: PropTypes.string,
  buttonLink: PropTypes.string,
  showButton: PropTypes.bool,
  alignment: PropTypes.string,
  background: PropTypes.string,
  isEditMode: PropTypes.bool,
  copyrightText: PropTypes.string,
};

const TeaserView = (props) => {
  const state = useTeaserData(props?.data);
  return <Teaser {...state} isEditMode={props.isEditMode} />;
};

TeaserView.propTypes = {
  data: PropTypes.object,
  isEditMode: PropTypes.bool,
};

export default TeaserView;
